import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  TablePagination,
  Grid,
  Rating,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { format } from "date-fns";
import { useDispatch, useSelector } from "react-redux";
import createInstance from "../../../api/axiosinstance";

const HRTable = () => {
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const [candidates, setCandidates] = useState([]);
  const [filteredCandidates, setFilteredCandidates] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [updatingDecision, setUpdatingDecision] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    roundId: null,
    decision: "",
  });

  const organizationId = user?.organizationId;
  const aiDisha = createInstance("DISHA");

  // Fetch all candidates feedback
  useEffect(() => {
    const fetchCandidates = async () => {
      try {
        setLoading(true);
        const response = await aiDisha.get(
          `/fetch_all_candidates_feedback?organization_id=${organizationId}`
        );
        const data = response.data;
        setCandidates(data);
        setFilteredCandidates(data);
        console.log("candidates", data);
      } catch (error) {
        console.error("Error fetching candidates:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCandidates();
  }, [organizationId]);

  // Handle search functionality
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredCandidates(candidates);
    } else {
      const lowercasedSearch = searchTerm.toLowerCase();
      const filtered = candidates.filter(
        (candidate) =>
          candidate.candidate_name.toLowerCase().includes(lowercasedSearch) ||
          candidate.candidate_id.toLowerCase().includes(lowercasedSearch) ||
          candidate.job_title.toLowerCase().includes(lowercasedSearch) ||
          candidate.interviewer_name.toLowerCase().includes(lowercasedSearch)
      );
      setFilteredCandidates(filtered);
    }
    setPage(0); // Reset to first page when searching
  }, [searchTerm, candidates]);

  // Handle HR Decision Change
  const handleHRDecisionChange = async (roundId, decision) => {
    try {
      setUpdatingDecision({ ...updatingDecision, [roundId]: true });

      const response = await aiDisha.patch(
        `/Hr_decision?round_id=${roundId}&decision=${decision}`
      );

      if (response.status === 200) {
        // Update the local state to reflect the change
        const updatedCandidates = candidates.map((candidate) => {
          if (candidate.round_id === roundId) {
            return { ...candidate, hr_decision: decision };
          }
          return candidate;
        });

        setCandidates(updatedCandidates);
        setFilteredCandidates(
          updatedCandidates.filter((candidate) => {
            if (searchTerm.trim() === "") return true;
            const lowercasedSearch = searchTerm.toLowerCase();
            return (
              candidate.candidate_name
                .toLowerCase()
                .includes(lowercasedSearch) ||
              candidate.candidate_id.toLowerCase().includes(lowercasedSearch) ||
              candidate.job_title.toLowerCase().includes(lowercasedSearch) ||
              candidate.interviewer_name
                .toLowerCase()
                .includes(lowercasedSearch)
            );
          })
        );

        setSnackbar({
          open: true,
          message: "HR Decision updated successfully!",
          severity: "success",
        });
      }
    } catch (error) {
      console.error("Error updating HR decision:", error);
      setSnackbar({
        open: true,
        message: "Failed to update HR decision. Please try again.",
        severity: "error",
      });
    } finally {
      setUpdatingDecision({ ...updatingDecision, [roundId]: false });
    }
  };

  // Handle snackbar close
  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  // Fetch specific candidate feedback
  const fetchCandidateFeedback = async (candidateId) => {
    try {
      const response = await fetch(
        `http://194.195.114.60:8000/get_candidates_feedback?candidate_id=${candidateId}&organization_id=${organizationId}`
      );
      if (!response.ok) {
        throw new Error("Failed to fetch candidate feedback");
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error(
        `Error fetching feedback for candidate ${candidateId}:`,
        error
      );
      return [];
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  // Function to format date nicely
  const formatDate = (dateString) => {
    if (!dateString) return "—";

    try {
      const date = new Date(dateString);

      if (isNaN(date.getTime())) return "Invalid date";

      return date.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false, // ✅ 24-hour format
      });
    } catch {
      return "Invalid date";
    }
  };

  // Get appropriate color for decision chip
  const getDecisionColor = (decision) => {
    switch (decision) {
      case "Strong Hire":
        return "success";
      case "Hire":
        return "primary";
      case "No Hire":
        return "error";
      default:
        return "default";
    }
  };

  // Get appropriate color for HR decision chip
  const getHRDecisionColor = (decision) => {
    switch (decision) {
      case "Hire":
        return "success";
      case "No Hire":
        return "error";
      case "Next Round":
        return "warning";
      default:
        return "default";
    }
  };

  // Calculate average rating for a candidate
  const calculateAverageRating = (candidate) => {
    return candidate.total_score / 5; // Divide total score by number of categories (5)
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Candidate Interview Feedback
        </Typography>

        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search by candidate name, ID, job title, or interviewer..."
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiInputBase-input::placeholder": {
                color: "#033538", // Change this to your desired color
                opacity: 1, // Override the default opacity
              },
            }}
          />
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredCandidates.length === 0 ? (
          <Paper sx={{ p: 3, textAlign: "center" }}>
            <Typography>
              No candidates found matching your search criteria.
            </Typography>
          </Paper>
        ) : (
          <>
            <TableContainer
              component={Paper}
              sx={{ backgroundColor: "#033538", color: "white" }}
            >
              <Table sx={{ minWidth: 750 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: "white" }}>Candidate</TableCell>
                    <TableCell sx={{ color: "white" }}>Job Title</TableCell>
                    <TableCell sx={{ color: "white" }}>
                      Interview Round
                    </TableCell>
                    <TableCell sx={{ color: "white" }}>Interviewer</TableCell>
                    <TableCell sx={{ color: "white" }} align="center">
                      Date & Time
                    </TableCell>
                    <TableCell sx={{ color: "white" }} align="center">
                      Overall Rating
                    </TableCell>
                    <TableCell sx={{ color: "white" }} align="center">
                      Decision
                    </TableCell>
                    <TableCell sx={{ color: "white" }} align="center">
                      HR Decision
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredCandidates
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((candidate) => (
                      <React.Fragment key={candidate._id}>
                        <TableRow hover>
                          <TableCell sx={{ color: "white" }}>
                            <Typography variant="subtitle2">
                              {candidate.candidate_name}
                            </Typography>
                            <Typography variant="caption" color="white">
                              {candidate.candidate_id}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ color: "white" }}>
                            {candidate.job_title}
                          </TableCell>
                          <TableCell sx={{ color: "white" }}>
                            {candidate.round}
                          </TableCell>
                          <TableCell sx={{ color: "white" }}>
                            {candidate.interviewer_name}
                          </TableCell>
                          <TableCell sx={{ color: "white" }} align="center">
                            {formatDate(candidate.date_and_time)}
                          </TableCell>
                          <TableCell align="center">
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <Rating
                                value={calculateAverageRating(candidate)}
                                precision={0.5}
                                readOnly
                                size="small"
                              />
                              <Typography variant="body2" sx={{ ml: 1 }}>
                                ({candidate.total_score}/25)
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={candidate.decision}
                              color={getDecisionColor(candidate.decision)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="center">
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                              <Select
                                value={candidate.Hr_decision || ""}
                                onChange={(e) =>
                                  setConfirmDialog({
                                    open: true,
                                    roundId: candidate.round_id,
                                    decision: e.target.value,
                                  })
                                }
                                displayEmpty
                                disabled={updatingDecision[candidate.round_id]}
                                sx={{
                                  color: "white",
                                  "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "red",
                                  },
                                  "&:hover .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "white",
                                  },
                                  "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                    {
                                      borderColor: "white",
                                    },
                                  "& .MuiSelect-icon": {
                                    color: "white",
                                  },
                                  "& .MuiMenuItem-root": {
                                    color: "red", // Set text color to black
                                  },
                                }}
                              >
                                <MenuItem
                                  value=""
                                  sx={{ color: "blue", opacity: "80%" }}
                                >
                                  Select Decision
                                </MenuItem>
                                <MenuItem sx={{ color: "black" }} value="Hire">
                                  Hire
                                </MenuItem>
                                <MenuItem
                                  sx={{ color: "black" }}
                                  value="No Hire"
                                >
                                  No Hire
                                </MenuItem>
                                <MenuItem
                                  sx={{ color: "black" }}
                                  value="Next Round"
                                >
                                  Next Round
                                </MenuItem>
                              </Select>
                            </FormControl>
                            {candidate.hr_decision && (
                              <Box sx={{ mt: 1 }}>
                                <Chip
                                  label={candidate.hr_decision}
                                  color={getHRDecisionColor(
                                    candidate.hr_decision
                                  )}
                                  size="small"
                                />
                              </Box>
                            )}
                            {updatingDecision[candidate.round_id] && (
                              <Box sx={{ mt: 1 }}>
                                <CircularProgress size={16} />
                              </Box>
                            )}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: "#006A71" }}>
                          <TableCell
                            colSpan={8}
                            sx={{ py: 0, borderBottom: "none" }}
                          >
                            <Accordion sx={{ backgroundColor: "#006A71" }}>
                              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="subtitle2">
                                  Detailed Feedback
                                </Typography>
                              </AccordionSummary>
                              <AccordionDetails>
                                <Grid container spacing={3}>
                                  <Grid item xs={12} md={6}>
                                    <Typography
                                      variant="subtitle2"
                                      gutterBottom
                                    >
                                      Technical Knowledge
                                    </Typography>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        mb: 1,
                                      }}
                                    >
                                      <Rating
                                        value={
                                          candidate.Technical_knowledge[0]
                                            .rating
                                        }
                                        readOnly
                                        size="small"
                                      />
                                      <Typography
                                        variant="body2"
                                        sx={{ ml: 1 }}
                                      >
                                        (
                                        {
                                          candidate.Technical_knowledge[0]
                                            .rating
                                        }
                                        /5)
                                      </Typography>
                                    </Box>
                                    <Typography variant="body2">
                                      {candidate.Technical_knowledge_detail}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={12} md={6}>
                                    <Typography
                                      variant="subtitle2"
                                      gutterBottom
                                    >
                                      Hands-on Skills
                                    </Typography>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        mb: 1,
                                      }}
                                    >
                                      <Rating
                                        value={
                                          candidate.Hands_on_skills[0].rating
                                        }
                                        readOnly
                                        size="small"
                                      />
                                      <Typography
                                        variant="body2"
                                        sx={{ ml: 1 }}
                                      >
                                        ({candidate.Hands_on_skills[0].rating}
                                        /5)
                                      </Typography>
                                    </Box>
                                    <Typography variant="body2">
                                      {candidate.Hands_on_skills_detail}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={12} md={6}>
                                    <Typography
                                      variant="subtitle2"
                                      gutterBottom
                                    >
                                      Problem Solving
                                    </Typography>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        mb: 1,
                                      }}
                                    >
                                      <Rating
                                        value={
                                          candidate.Problem_solving[0].rating
                                        }
                                        readOnly
                                        size="small"
                                      />
                                      <Typography
                                        variant="body2"
                                        sx={{ ml: 1 }}
                                      >
                                        ({candidate.Problem_solving[0].rating}
                                        /5)
                                      </Typography>
                                    </Box>
                                    <Typography variant="body2">
                                      {candidate.Problem_solving_detail}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={12} md={6}>
                                    <Typography
                                      variant="subtitle2"
                                      gutterBottom
                                    >
                                      Communication Skills
                                    </Typography>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        mb: 1,
                                      }}
                                    >
                                      <Rating
                                        value={
                                          candidate.Communication_skills[0]
                                            .rating
                                        }
                                        readOnly
                                        size="small"
                                      />
                                      <Typography
                                        variant="body2"
                                        sx={{ ml: 1 }}
                                      >
                                        (
                                        {
                                          candidate.Communication_skills[0]
                                            .rating
                                        }
                                        /5)
                                      </Typography>
                                    </Box>
                                    <Typography variant="body2">
                                      {candidate.Communication_skills_detail}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={12} md={6}>
                                    <Typography
                                      variant="subtitle2"
                                      gutterBottom
                                    >
                                      Culture Fit
                                    </Typography>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        mb: 1,
                                      }}
                                    >
                                      <Rating
                                        value={candidate.Culture_fit[0].rating}
                                        readOnly
                                        size="small"
                                      />
                                      <Typography
                                        variant="body2"
                                        sx={{ ml: 1 }}
                                      >
                                        ({candidate.Culture_fit[0].rating}/5)
                                      </Typography>
                                    </Box>
                                    <Typography variant="body2">
                                      {candidate.Culture_fit_detail}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={12}>
                                    <Box
                                      sx={{
                                        mt: 2,
                                        p: 2,
                                        bgcolor: "#f5f5f5",
                                        borderRadius: 1,
                                        color: "black",
                                      }}
                                    >
                                      <Typography
                                        variant="subtitle2"
                                        gutterBottom
                                      >
                                        Additional Comments
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        gutterBottom
                                        sx={{ color: "black" }}
                                      >
                                        Next Round Recommendation:
                                        {candidate.next_round_recommendation}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        sx={{ color: "black" }}
                                      >
                                        Remarks:
                                        {candidate.remarks}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                </Grid>
                              </AccordionDetails>
                            </Accordion>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredCandidates.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                color: "#033538",
                "& .MuiTablePagination-toolbar": {
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end", // Aligns content to the right
                  "& .MuiTablePagination-spacer": {
                    flex: "none", // Reduces spacer impact
                  },
                },
                "& .MuiTablePagination-selectLabel": {
                  color: "#033538",
                  margin: "0 10px 0 0", // Adjust spacing
                },
                "& .MuiTablePagination-select": {
                  color: "#033538",
                  marginRight: "10px", // Adds space after dropdown
                },
                "& .MuiTablePagination-displayedRows": {
                  color: "#033538",
                  margin: "0 20px", // Centering the page info text
                },
                "& .MuiTablePagination-actions": {
                  color: "#033538",
                  marginLeft: "10px", // Space before navigation buttons
                },
              }}
            />
          </>
        )}
      </Box>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      <Dialog
        open={confirmDialog.open}
        onClose={() =>
          setConfirmDialog({ open: false, roundId: null, decision: "" })
        }
      >
        <DialogTitle sx={{ fontWeight: "bold" }}>
          Confirm HR Decision
        </DialogTitle>

        <DialogContent>
          <Typography sx={{color:"black"}}>
            Are you sure you want to proceed with this decision?👇
          </Typography>
          <Typography sx={{ mt: 1, fontWeight: 500 ,color:"black"}}>
            <strong style={{color:"blue"}}>{confirmDialog.decision}</strong>
          </Typography>
          <Typography sx={{ mt: 1, color: "error.main" }}>
            An email will be sent to the candidate.
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() =>
              setConfirmDialog({ open: false, roundId: null, decision: "" })
            }
            color="inherit"
            sx={{color:"grey"}}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              handleHRDecisionChange(
                confirmDialog.roundId,
                confirmDialog.decision
              );
              setConfirmDialog({ open: false, roundId: null, decision: "" });
            }}
          >
            Confirm & Send
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default HRTable;
